import * as React from "react";
import { useNavigate } from "react-router-dom";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { Autocomplete, TextField, Switch, Tooltip } from "@mui/material";
import { TableHeadComponent } from "../../../common/TableHead";
import { integrationsMatcher } from "../../../utils/integrationsMatcher";
import { useAppStore } from "../../../../appStore";
import { useFeatureFlag } from "../../../../hooks/useFeatureFlag";
import { OrangeTextButton } from "../../../common/OrangeTextButton";
import { SeeNotesDialog } from "./SeeNotesDialog";
import { epochToUPCCovertor } from "./SiteAboutPageUtils/epochToUPCCovertor";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { timeSinceConvertor } from "./SiteAboutPageUtils/timeSinceConvertor";
import { useSiteAboutStore } from "./siteAboutStore/siteAboutStore";
import { safeGetItem } from "../../../../utils/localStorageUtils";
import { handleSubmitUpdatedNotes } from "./SiteAboutPageUtils/handleSubmitUpdatedNotes";
import type {
  PatchObject,
  SiteInfo,
} from "./SiteAboutPageUtils/siteAboutInterfaces";
import { NatWireguardConflictDialog } from "./NatWireguardConflictDialog";
import { useNatWireguardConflictDialog } from "./SiteAboutPageUtils/useNatWireguardConflictDialog";

interface GroupOption {
  id: number;
  name: string;
  parent: number;
  parent_account: boolean;
}

interface AlarmOption {
  name: string;
  value: string | number;
}

export const GeneralInfoTabAutoPatrol = ({
  editMode,
}: {
  editMode: boolean;
}) => {
  const navigate = useNavigate();
  const { wireGuardOptions } = useAppStore();
  const { isFeatureEnabled } = useFeatureFlag();
  const showWireguard = isFeatureEnabled("showWireguard", false);

  const {
    siteInfo,
    setSiteInfo,
    generalInfoTabPatchObject,
    setGeneralInfoTabPatchObject,
    setGeneralInfoTabChangesMade,
    vCHOnlySchedules,
  } = useSiteAboutStore();
  const shouldShowWireguardAvailabilityZone =
    showWireguard && Boolean(siteInfo.display_wireguard);

  const setSiteInfoCompat = React.useCallback(
    (update: React.SetStateAction<SiteInfo>) => {
      const current = useSiteAboutStore.getState().siteInfo;
      const next =
        typeof update === "function"
          ? (update as (prev: SiteInfo) => SiteInfo)(current)
          : update;
      setSiteInfo(next);
    },
    [setSiteInfo]
  );

  const setPatchObjectCompat = React.useCallback(
    (update: React.SetStateAction<PatchObject>) => {
      const current = useSiteAboutStore.getState().generalInfoTabPatchObject;
      const next =
        typeof update === "function"
          ? (update as (prev: PatchObject) => PatchObject)(current)
          : update;
      setGeneralInfoTabPatchObject(next);
    },
    [setGeneralInfoTabPatchObject]
  );

  const {
    ecsNatInstance,
    wireguard: wireguardId,
    wireguardAutocompleteKey,
    handleWireguardSelection,
    handleNatToggle,
    conflictDialogProps,
  } = useNatWireguardConflictDialog({
    siteInfo,
    showWireguardFlag: showWireguard,
    enabled: editMode && !vCHOnlySchedules,
    setSiteInfo: setSiteInfoCompat,
    setPatchObject: setPatchObjectCompat,
    setChangesMade: setGeneralInfoTabChangesMade,
  });

  const selectedWireguardName =
    wireGuardOptions.find((option) => option.id === wireguardId)?.name ?? null;

  const [seeNotesDialogOpen, setSeeNotesDialogOpen] = React.useState(false);
  const [noteText, setNoteText] = React.useState(siteInfo.notes ?? "");

  const groupOptions: GroupOption[] = (
    safeGetItem<GroupOption[]>("parentGroups", []) ?? []
  ).filter((group: GroupOption) => group.parent_account === true);

  const groupOptionsIds: Map<number, string> = new Map();
  const groupOptionsNames: Map<string, number> = new Map();
  const groupStandardNames: Map<string, string> = new Map();

  groupOptions.forEach((option: GroupOption) => {
    groupOptionsIds.set(option.id, `${option.name} (group id: ${option.id})`);
  });

  groupOptions.forEach((option: GroupOption) => {
    groupOptionsNames.set(`${option.name} (group id: ${option.id})`, option.id);
  });

  groupOptions.forEach((option: GroupOption) => {
    groupStandardNames.set(
      `${option.name} (group id: ${option.id})`,
      option.name
    );
  });

  const alarmOptions = safeGetItem<AlarmOption[]>("alarmOptions", []) ?? [];

  const alarmIds = new Map();
  alarmOptions.forEach((object: AlarmOption) => {
    alarmIds.set(object.value, object.name);
  });

  const alarmNames = new Map();
  alarmOptions.forEach((object: AlarmOption) => {
    alarmNames.set(object.name, object.value);
  });

  const titlesArray = [
    "Site Name",
    "Timezone",
    "Integration Type",
    "Parent Group",
  ];

  if (siteInfo.show_ecs_nat_instance) {
    titlesArray.push("Whitelisting");
  }

  // Single row titles for General Site Info (standard view)
  const generalInfoTitlesSingleRow = (() => {
    const base = ["Site Name", "Timezone", "Integration Type", "Parent Group"];
    if (siteInfo.show_ecs_nat_instance) base.push("Whitelisting");
    if (shouldShowWireguardAvailabilityZone)
      base.push("WireGuard Availability Zone");
    base.push("Last Synchronized", "Last Restart", "Last Alert", "Notes");
    return base;
  })();

  // Standard view (non-edit mode) — one table, one row
  const generalInfoTabAutoPatrol = (
    <div>
      {seeNotesDialogOpen && (
        <SeeNotesDialog
          seeNotesDialogOpen={seeNotesDialogOpen}
          setSeeNotesDialogOpen={setSeeNotesDialogOpen}
          defaultText={siteInfo.notes}
          onSubmitNotes={async (newNotes) => {
            const setSiteInfoCompat = (
              update: React.SetStateAction<SiteInfo>
            ) => {
              const current = useSiteAboutStore.getState().siteInfo;
              const next =
                typeof update === "function"
                  ? (update as (prev: SiteInfo) => SiteInfo)(current)
                  : update;
              useSiteAboutStore.setState({ siteInfo: next });
            };
            await handleSubmitUpdatedNotes(
              newNotes,
              siteInfo.notes ?? "",
              setSiteInfoCompat,
              undefined,
              navigate
            );
          }}
        />
      )}

      <TableContainer
        component={Paper}
        sx={{
          width: "100%",
          mb: 2,
          "& .MuiTableHead-root .MuiTableCell-root": { borderBottom: "none" },
        }}
      >
        <Table
          sx={{
            width: "100%",
            fontFamily: "Mulish",
            tableLayout: "fixed",
          }}
        >
          <TableHeadComponent titlesArray={generalInfoTitlesSingleRow} />
          <TableBody>
            <TableRow key={siteInfo.id}>
              <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
                {siteInfo.name}
              </TableCell>
              <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
                {siteInfo.timezone}
              </TableCell>
              <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
                {integrationsMatcher("Name", siteInfo.integration_type)}
              </TableCell>
              <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
                {siteInfo.breadcrumbs?.[siteInfo.breadcrumbs.length - 2] ||
                  siteInfo.parent_group?.name ||
                  "--"}
              </TableCell>
              {siteInfo.show_ecs_nat_instance && (
                <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
                  {siteInfo.ecs_nat_instance ? (
                    <Switch defaultChecked disabled={true} color="warning" />
                  ) : (
                    <Switch checked={false} disabled={true} color="warning" />
                  )}
                </TableCell>
              )}
              {shouldShowWireguardAvailabilityZone && (
                <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
                  {wireGuardOptions.find(
                    (option) => option.id === siteInfo.wireguard
                  )?.name || "--"}
                </TableCell>
              )}
              <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
                {siteInfo.deployed_date
                  ? epochToUPCCovertor(
                      siteInfo.deployed_date,
                      siteInfo.timezone
                    ).toString()
                  : "--"}
                &nbsp;
                {siteInfo.deployed_date && siteInfo.timezone ? (
                  <Tooltip
                    title={siteInfo.timezone}
                    placement="top"
                    sx={{ fontSize: "15px", fontFamily: "mulish" }}
                  >
                    <InfoOutlinedIcon fontSize="small"></InfoOutlinedIcon>
                  </Tooltip>
                ) : (
                  ""
                )}
              </TableCell>
              <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
                {siteInfo.pod_boot_time
                  ? epochToUPCCovertor(
                      siteInfo.pod_boot_time,
                      siteInfo.timezone
                    ).toString()
                  : "--"}
                &nbsp;
                {siteInfo.pod_boot_time && siteInfo.timezone ? (
                  <Tooltip
                    title={siteInfo.timezone}
                    placement="top"
                    sx={{ fontSize: "15px", fontFamily: "mulish" }}
                  >
                    <InfoOutlinedIcon fontSize="small"></InfoOutlinedIcon>
                  </Tooltip>
                ) : (
                  ""
                )}
                {siteInfo.pod_boot_time &&
                siteInfo.deployed_date &&
                siteInfo.deployed_date > siteInfo.pod_boot_time ? (
                  <Tooltip
                    title="Settings synced after last restart — rearm site to restart and apply changes."
                    placement="top"
                    sx={{ fontSize: "15px", fontFamily: "mulish" }}
                  >
                    <WarningAmberIcon
                      fontSize="small"
                      sx={{ color: "#ed6c02", verticalAlign: "middle" }}
                    />
                  </Tooltip>
                ) : (
                  ""
                )}
              </TableCell>
              <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
                {siteInfo.last_alert
                  ? timeSinceConvertor(siteInfo.last_alert)
                  : "--"}
              </TableCell>
              <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
                <OrangeTextButton
                  text="See Notes"
                  onClick={() => {
                    setSeeNotesDialogOpen(true);
                  }}
                  disabled={false}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );

  // Edit mode view
  const generalInfoTabAutoPatrolEditMode = (
    <div className="pb-[10px]">
      <NatWireguardConflictDialog {...conflictDialogProps} />
      <TableContainer
        component={Paper}
        sx={{
          "& .MuiTableHead-root .MuiTableCell-root": { borderBottom: "none" },
        }}
      >
        <Table
          sx={{ minWidth: 1000, fontFamily: "Mulish" }}
          aria-label="simple table"
        >
          <TableHeadComponent titlesArray={titlesArray} />
          <TableBody>
            <TableRow key={siteInfo.id}>
              <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
                {vCHOnlySchedules ? (
                  siteInfo.name
                ) : (
                  <div key={siteInfo.name}>
                    <TextField
                      defaultValue={siteInfo.name}
                      variant="standard"
                      sx={{ width: 200 }}
                      type="string"
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setGeneralInfoTabPatchObject({
                          ...generalInfoTabPatchObject,
                          name: event.target.value,
                        });
                        setSiteInfo({
                          ...siteInfo,
                          name: event.target.value,
                        });
                        setGeneralInfoTabChangesMade(true);
                      }}
                    />
                  </div>
                )}
              </TableCell>
              <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
                {vCHOnlySchedules ? (
                  siteInfo.timezone
                ) : (
                  <div key={siteInfo.timezone}>
                    <Autocomplete
                      disablePortal
                      options={safeGetItem<string[]>("timeZoneArray", []) ?? []}
                      defaultValue={siteInfo.timezone}
                      sx={{ width: 200 }}
                      onInputChange={(event, newInputValue) => {
                        setGeneralInfoTabPatchObject({
                          ...generalInfoTabPatchObject,
                          timezone: newInputValue,
                        });
                        setSiteInfo({
                          ...siteInfo,
                          timezone: newInputValue,
                        });
                        setGeneralInfoTabChangesMade(true);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="standard"
                          autoComplete="off"
                        />
                      )}
                    />
                  </div>
                )}
              </TableCell>
              <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
                {integrationsMatcher("Name", siteInfo.integration_type)}
              </TableCell>
              <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
                {vCHOnlySchedules ? (
                  siteInfo.breadcrumbs?.[siteInfo.breadcrumbs.length - 2] ||
                  siteInfo.parent_group?.name ||
                  "--"
                ) : (
                  <div key={siteInfo.breadcrumbs?.[0]}>
                    <Autocomplete
                      disablePortal
                      options={groupOptions.map((option: GroupOption) => {
                        return `${option.name} (group id: ${option.id})`;
                      })}
                      defaultValue={
                        siteInfo.breadcrumbs?.[siteInfo.breadcrumbs.length - 2]
                      }
                      sx={{ width: 150 }}
                      onInputChange={(event, newInputValue) => {
                        setGeneralInfoTabPatchObject({
                          ...generalInfoTabPatchObject,
                          parent_group: {
                            parent: groupOptionsNames.get(newInputValue),
                          },
                        });
                        setSiteInfo({
                          ...siteInfo,
                          parent_group: {
                            name: groupStandardNames.get(newInputValue),
                          },
                        });
                        localStorage.setItem("groupNameChange", "true");
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="standard"
                          autoComplete="off"
                        />
                      )}
                    />
                  </div>
                )}
              </TableCell>
              {siteInfo.show_ecs_nat_instance && (
                <TableCell>
                  {vCHOnlySchedules ? (
                    siteInfo.ecs_nat_instance ? (
                      <Switch defaultChecked disabled={true} color="warning" />
                    ) : (
                      <Switch checked={false} disabled={true} color="warning" />
                    )
                  ) : (
                    <Switch
                      checked={ecsNatInstance}
                      disabled={false}
                      color="warning"
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        handleNatToggle(event.target.checked);
                      }}
                    />
                  )}
                </TableCell>
              )}
            </TableRow>
          </TableBody>
        </Table>
        <Table
          sx={{ minWidth: 1000, fontFamily: "Mulish" }}
          aria-label="simple table"
        >
          <TableHeadComponent
            titlesArray={
              shouldShowWireguardAvailabilityZone
                ? ["WireGuard Availability Zone", "Notes", "", "", "", ""]
                : ["Notes", "", "", "", "", ""]
            }
          />
          <TableBody>
            <TableRow key={siteInfo.id}>
              {shouldShowWireguardAvailabilityZone && (
                <TableCell
                  align="left"
                  sx={{ fontFamily: "Mulish", verticalAlign: "top" }}
                >
                  {vCHOnlySchedules ? (
                    wireGuardOptions.find(
                      (option) => option.id === siteInfo.wireguard
                    )?.name || "--"
                  ) : (
                    <Autocomplete
                      key={wireguardAutocompleteKey}
                      disablePortal
                      options={wireGuardOptions.map((option) => option.name)}
                      value={selectedWireguardName}
                      sx={{ width: 300 }}
                      onChange={(_event, newValue) => {
                        if (!newValue) {
                          return;
                        }
                        const selectedOption = wireGuardOptions.find(
                          (option) => option.name === newValue
                        );
                        if (selectedOption) {
                          handleWireguardSelection(selectedOption.id);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="standard"
                          autoComplete="off"
                        />
                      )}
                    />
                  )}
                </TableCell>
              )}
              <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
                <TextField
                  onChange={(event) => {
                    setGeneralInfoTabPatchObject({
                      ...generalInfoTabPatchObject,
                      notes: event.target.value,
                    });
                    setSiteInfo({
                      ...siteInfo,
                      notes: event.target.value,
                    });
                    setNoteText(event.target.value);
                    setGeneralInfoTabChangesMade(true);
                  }}
                  value={noteText}
                  variant="outlined"
                  multiline
                  rows={6}
                  sx={{ fontFamily: "Mulish", width: 400 }}
                />
              </TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              {!shouldShowWireguardAvailabilityZone && (
                <TableCell></TableCell>
              )}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );

  return editMode ? generalInfoTabAutoPatrolEditMode : generalInfoTabAutoPatrol;
};
